import { create, convert } from "xmlbuilder2";
import { ROOT_TAG_NAME, EXPORT_CONFIG, IMPORT_CONFIG } from "./mismo_config";
import { fake_state_todo_remove as default_document } from "../constants/fake_state_todo_remove";
import { deep_clone_simple } from "../constants/utils";
import { parseRelationships } from "./relationships";

/**
 * If the given value is a function, invoke it and pass the given context as
 * the first and only argument, returning the value returned from the function.
 * Otherwise, this simply returns the given value.
 */
function invokeOrValue(value, context) {
  return (typeof value === "function") ? value(context) : value;
}

function createNode(builder, tagName, configObj, inContext) {
  const {
    "@forEach": forEach,
    ...configObjRest
  } = configObj;

  if (forEach) {
    const iterable = forEach(inContext);
    let index = 0;

    for (const item of iterable) {
      const context = {
        ...inContext,
        location: `${inContext.location}[${index}]`
      };

      if (typeof item === "object" && !!item) {
        Object.assign(context, item);
      } else {
        context.item = item;
      }

      createNode(builder, tagName, configObjRest, context);

      index++;
    }

    return;
  }

  console.log(`Creating ${inContext.location}`);

  const {
    "@skip": skip,
    "@att": att,
    "@txt": txt,
    "@populate": populate,
    ...children
  } = configObjRest;

  if (skip && skip(inContext)) {
    console.log(`Skipping ${inContext.location}`);
    return;
  }

  const newBuilder = builder.ele(tagName);

  if (att) {
    newBuilder.att(invokeOrValue(att, inContext));
  }

  if (txt) {
    newBuilder.txt(invokeOrValue(txt, inContext));
  }

  if (populate) {
    const populateArr = Array.isArray(populate) ? populate : [populate];
    for (const populateFn of populateArr) {
      populateFn(newBuilder, inContext);
    }
  }

  if (children) {
    for (const [childTagName, childObj] of Object.entries(children)) {
      createNode(newBuilder, childTagName, childObj, {
        ...inContext,
        location: `${inContext.location}.${childTagName}`
      });
    }
  }
}

export function toMismoXml(document, options) {
  const rootBuilder = create({ version: "1.0", encoding: options.encoding });
  const baseContext = {
    document,
    options,
    // The LO's Admin profile (account + company) - not on the loan document, so
    // the export menu passes them through options for the Loan Originator party.
    accountProfile: options.accountProfile,
    companyProfile: options.companyProfile,
    location: ROOT_TAG_NAME
  };

  createNode(rootBuilder, ROOT_TAG_NAME, EXPORT_CONFIG, baseContext);

  return rootBuilder.end({
    prettyPrint: true,
    allowEmptyTags: true
  });
}

function importNode(obj, tagName, configObj, inContext) {
  const {
    "@required": required,
    "@requireOne": requireOne,
    ...configObjRest
  } = configObj;

  if (obj === undefined || (Array.isArray(obj) && obj.length === 0)) {
    if (required || requireOne) {
      throw new Error(`Missing required element: ${inContext.location}`);
    }

    return;    
  }

  if (Array.isArray(obj)) {
    console.log(`is array: ${inContext.location}`);

    if (obj.length > 1 && requireOne) {
      throw new Error(
        `There must be only 1 ${inContext.location} ` +
        `element but there are ${obj.length}`
      );
    }

    let index = 0;

    for (const objItem of obj) {
      importNode(objItem, tagName, configObj, {
        ...inContext,
        location: `${inContext.location}[${index}]`
      });

      index++;
    }

    return;
  }

  console.log(`Visiting ${inContext.location}`);

  const {
    "@skip": skip,
    "@item": item,
    "@populate": populate,
    ...children
  } = configObjRest;

  if (skip && skip(obj, inContext)) {
    console.log("Skipping", obj);
    return;
  }

  const context = { ...inContext };

  if (item) {
    const itemResult = item(obj, context);

    if (typeof itemResult === "object" && !!itemResult) {
      Object.assign(context, itemResult);
    } else {
      context.item = itemResult;
    }
  }

  if (populate) {
    populate(obj, context);
  }

  for (const [childTagName, childConfigObj] of Object.entries(children)) {
    importNode(obj[childTagName], childTagName, childConfigObj, {
      ...context,
      location: `${context.location}.${childTagName}`
    });
  }
}

// Elements whose mere PRESENCE means something to the importer, so they are
// kept even when empty: BORROWER marks a party as a borrower (isBorrowerParty),
// and INDIVIDUAL is what makes a PARTY a person — dropping an empty one would
// drop the whole borrower along with their income, assets and debts.
const KEEP_WHEN_EMPTY = new Set(["BORROWER", "INDIVIDUAL"]);

// MISMO amount / percent / count fields must hold a plain number, but some
// senders format them ("1,200.00", "$950", " 12 "). Decimal rejects all of
// those, so clean them to the bare number. A value that is already a plain
// number (every clean LOS file) is left exactly as it is.
const NUMERIC_FIELD = /(Amount|Percent|Count)$/;
const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;

function cleanNumber(value) {
  if (PLAIN_NUMBER.test(value)) {
    return value;
  }
  const stripped = value.replace(/[\s$,%]/g, "");
  return PLAIN_NUMBER.test(stripped) ? stripped : value;
}

/**
 * Normalize the parsed XML before the import reads it.
 *
 * xmlbuilder2 turns an empty element — <PostalCode/>, <PostalCode></PostalCode>
 * or <PostalCode> </PostalCode> — into an empty object {}, not "". That {} is
 * truthy and not null, so it passed every importer check and landed in the loan
 * file, where it crashed pages ("This page couldn't be displayed"), threw in
 * the Decimal math, or became "[object Object]" (which merged every debt with
 * a blank account # into one row). Intake websites (220) and some LOSs write
 * empty tags for anything left blank, so we fix it once, here, for every field:
 *
 *  - an empty element is removed, so the importer sees it as simply absent —
 *    the case it already handles for every section a file leaves out;
 *  - a text element that also carries attributes ({ "#": "5", "@x": ... })
 *    becomes its plain text; nothing reads attributes on a text field;
 *  - amount / percent / count text is cleaned to a plain number.
 *
 * A file with none of these (a clean POINT / DU / LANIS export) comes back
 * unchanged, so its import result is identical to before.
 */
function normalizeXmlTree(node, key) {
  if (Array.isArray(node)) {
    const kept = node
      .map(item => normalizeXmlTree(item, key))
      .filter(item => item !== undefined);
    return kept.length > 0 ? kept : undefined;
  }

  if (typeof node === "string") {
    return key && NUMERIC_FIELD.test(key) ? cleanNumber(node) : node;
  }

  if (!node || typeof node !== "object") {
    return node;
  }

  const keys = Object.keys(node);
  if (keys.includes("#") && keys.every(k => k === "#" || k.startsWith("@"))) {
    return normalizeXmlTree(String(node["#"]), key);
  }

  const out = {};
  for (const childKey of keys) {
    const child = childKey.startsWith("@")
      ? node[childKey]
      : normalizeXmlTree(node[childKey], childKey);
    if (child !== undefined) {
      out[childKey] = child;
    }
  }

  if (Object.keys(out).length === 0 && !KEEP_WHEN_EMPTY.has(key)) {
    return undefined;
  }
  return out;
}

export function fromMismoXml(xmlString, options) {
  const obj = normalizeXmlTree(convert(xmlString, { format: "object" })) || {};

  console.log(obj);

  const rootKeys = Object.keys(obj);
  if (rootKeys.length !== 1 || rootKeys[0] !== ROOT_TAG_NAME) {
    throw new Error(`Expecting 1 root ${ROOT_TAG_NAME} element, got [${rootKeys.join(", ")}]`);
  }

  const document = deep_clone_simple(default_document);

  // Ownership (which borrower owns which asset/debt) and the income->employer
  // link live in the DEAL's RELATIONSHIPS block, which sits AFTER the records
  // it describes. Pre-scan it so the maps are available while we traverse.
  let deal;
  try {
    deal = obj[ROOT_TAG_NAME].DEAL_SETS.DEAL_SET.DEALS.DEAL;
  } catch (e) {
    deal = undefined;
  }

  const relationships = parseRelationships(deal);

  const baseContext = {
    document,
    options,
    relationships,
    // Employment income amounts, keyed by employer label. Income items are
    // visited before EMPLOYERS within a borrower, so we stash the amounts here
    // and apply them as each employer is created.
    employerIncome: {},
    // Dedup indexes shared across sibling nodes (the traversal shallow-copies
    // the context per node, so these must live on the base context to persist).
    // A joint debt/REO reaches us as multiple elements or multiple arcs; these
    // collapse it to one row and union the owners.
    liabilityByKey: {},
    reoByKey: {},
    // Source MISMO label -> imported record, so the property<->mortgage arcs
    // (ASSET_IsAssociatedWith_LIABILITY) can be re-attached after traversal.
    reoAssetLabels: {},
    liabilityLabels: {},
    location: ROOT_TAG_NAME
  };

  importNode(obj[ROOT_TAG_NAME], ROOT_TAG_NAME, IMPORT_CONFIG, baseContext);

  // Link co-borrowers who share Borrower 1's current address ("Same address as").
  const borrowers = document.applicant.borrowers;
  if (borrowers.length > 1) {
    const primary = borrowers[0];
    for (let i = 1; i < borrowers.length; i++) {
      const b = borrowers[i];
      if (
        primary.current_address &&
        b.current_address === primary.current_address &&
        b.city === primary.city &&
        b.state === primary.state &&
        b.zip_code === primary.zip_code
      ) {
        b.same_address_as = "0";
      }
    }
  }

  // Re-attach mortgages to their properties from the
  // ASSET_IsAssociatedWith_LIABILITY arcs. The arc names a property (asset)
  // label and a liability label; map both back to the imported records and set
  // the debt's attached_properties to the property's "reo:<index>" key.
  const assetLiabilities = relationships.assetLiabilities || {};
  Object.entries(assetLiabilities).forEach(([assetLabel, liabilityLabels]) => {
    const property = baseContext.reoAssetLabels[assetLabel];
    if (!property) return;
    const propIndex = document.investment_properties.indexOf(property);
    if (propIndex < 0) return;
    const propKey = `reo:${propIndex}`;

    liabilityLabels.forEach(liabilityLabel => {
      const account = baseContext.liabilityLabels[liabilityLabel];
      if (!account) return;
      if (!Array.isArray(account.attached_properties)) {
        account.attached_properties = [];
      }
      if (!account.attached_properties.includes(propKey)) {
        account.attached_properties.push(propKey);
      }
    });
  });

  // Every loan needs a term: the payment/amortization math does
  // new Decimal(loan.term), so a blank term crashes Debt, Down Payment, LE Price
  // Eng and every other page that prices the loan. Intake applications (e.g. a
  // 220 website app) send no AMORTIZATION_RULE, so default to 30 years — the
  // same rule the Fannie 3.2 importer uses (fnm/importers/loan.js). The LO sees
  // and can change it on the Loan Type page.
  document.loans.forEach(loan => {
    if (!loan.term) {
      loan.term = "30";
    }
  });

  // TODO: Support `documentType`
  return { document, documentType: null };
}
